import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import ColorModal from './ColorModal';
import confirmComposerExit from './confirmComposerExit';
import { extend, override } from 'flarum/common/extend';
import Picker from './Picker';
import PhotoState from './State';
import PhotoModal from './PhotoModal';
import t from '../../common/t';
function initialize(component) {
  const owner = app.session.user?.id(),
    post = component.attrs.post,
    config = app.forum.attribute('ccotPostImages');
  const prior = component.composer.fields.ccotPhotos;
  if (prior && prior.owner !== owner) {
    component.composer.fields.content('');
    component.composer.fields.title?.('');
    component.composer.fields.ccotPhotos = null;
    component.ccotPhotos = null;
    return; // Never initialize another account's editor from an old post model/draft.
  }
  if (!owner || !config) return;
  const editor = post?.attribute('ccotImageEditor'),
    simple = config.simple && (!post || editor?.simple === true);
  const context = post ? {
    post_id: post.id()
  } : component.attrs.discussion ? {
    discussion_id: component.attrs.discussion.id()
  } : {};
  const key = post ? 'post:' + post.id() : component.attrs.discussion ? 'reply:' + component.attrs.discussion.id() : 'discussion';
  if (!simple && !(prior?.simple && prior.composerKey === key)) {
    component.composer.fields.ccotPhotos = null;
    component.ccotPhotos = null;
    component.composer.fields.ccotPhotoWarning = !!config.simple && !!post && editor?.simple !== true;
    return;
  }
  component.composer.fields.ccotPhotoWarning = false;
  const images = (post?.attribute('ccotPostImages') || []).map(image => ({
    ...image,
    markup: editor?.tokens?.[image.id]
  }));
  const state = prior && prior.owner === owner && prior.composerKey === key ? prior : new PhotoState(owner, images, context, true, component.composer);
  state.composerKey = key;
  component.composer.fields.ccotPhotos = state;
  component.ccotPhotos = state;
  if (post && simple && prior !== state) {
    state.originalText = editor.text;
    state.grouped = editor.grouped;
    component.composer.fields.content(editor.text);
  }
}
export default function installPhotoComposer() {
  confirmComposerExit();
  // Give native live preview the same composed source as save, without putting
  // image markup into the actual textarea or replacing the native formatter.
  for (const method of ['oncreate', 'onupdate']) extend('flarum/forum/components/ComposerPostPreview', method, function () {
    const original = this.attrs.composer.ccotPreviewSource || this.attrs.composer,
      state = original.fields.ccotPhotos;
    if (!state?.simple) return;
    const composer = Object.create(original);
    composer.ccotPreviewSource = original;
    composer.fields = {
      ...original.fields,
      content: () => state.compose(original.fields.content())
    };
    this.attrs = {
      ...this.attrs,
      composer
    };
  });
  override('flarum/forum/components/PostStream', 'onscroll', function (original, ...args) {
    // A fixed iOS body reports zero scroll while the photo viewer is open.
    if (document.body.style.position === 'fixed' && document.querySelector('.CCOT-photo-modal')) {
      clearTimeout(this.calculatePositionTimeout);
      return;
    }
    return original(...args);
  });
  for (const name of ['DiscussionComposer', 'ReplyComposer', 'EditPostComposer']) {
    const target = 'flarum/forum/components/' + name;
    extend(target, 'oninit', function () {
      initialize(this);
    });
    extend(target, 'data', function (data) {
      if (this.ccotPhotos) data.content = this.ccotPhotos.compose(data.content);
    });
    override(target, 'hasChanges', function (original) {
      if (this.attrs.post && this.ccotPhotos?.simple) return this.composer.fields.content() !== this.ccotPhotos.originalText || this.ccotPhotos.changed();
      return original() || !!this.ccotPhotos?.changed();
    });
    override(target, 'onsubmit', function (original, ...args) {
      if (this.loading) return;
      if (this.ccotPhotos?.pending()) {
        this.ccotPhotos.error = t('lib.post_images_wait');
        m.redraw();
        return;
      }
      return original(...args);
    });
  }
  extend('flarum/common/components/TextEditor', 'view', function (vnode) {
    const fields = this.attrs.composer?.fields,
      state = fields?.ccotPhotos;
    if (!state && !fields?.ccotPhotoWarning) return;
    const queue = [vnode];
    while (queue.length) {
      const parent = queue.shift();
      const children = parent?.children;
      if (!Array.isArray(children)) continue;
      const at = children.findIndex(child => child?.tag === 'ul' && child.attrs?.className?.includes('TextEditor-controls'));
      if (at !== -1) {
        children.splice(at, 0, state ? m(Picker, {
          state,
          disabled: this.attrs.disabled
        }) : m('p.CCOT-help', {
          role: 'status'
        }, t('lib.post_images_unrecognized')));
        return;
      }
      queue.push(...children.filter(child => child && typeof child === 'object'));
    }
  });
  extend('flarum/common/components/TextEditor', 'toolbarItems', function (items) {
    const state = this.attrs.composer?.fields.ccotPhotos;
    if (!state?.simple || !app.forum.attribute('ccotTextFormatting')?.color) return;
    items.add('ccot-color', m(Button, {
      type: 'button',
      className: 'Button Button--icon',
      icon: 'fas fa-palette',
      'aria-label': t('lib.post_images_color'),
      title: t('lib.post_images_color'),
      disabled: this.attrs.disabled,
      onclick: () => {
        const composer = this.attrs.composer,
          editor = composer.editor,
          selection = editor.getSelectionRange();
        app.modal.show(ColorModal, {
          composer,
          selection,
          text: composer.fields.content()
        });
      }
    }), -10);
  });
  extend('flarum/common/components/TextEditor', 'controlItems', function (items) {
    const state = this.attrs.composer?.fields.ccotPhotos;
    if (!state) return;
    const submit = items.get('submit');
    if (submit?.attrs) submit.attrs.disabled = !!this.attrs.disabled || state.pending();
  });
  for (const method of ['oncreate', 'onupdate']) extend('flarum/forum/components/CommentPost', method, function () {
    const images = this.attrs.post.attribute('ccotPostImages') || [];
    for (const image of images) {
      for (const node of this.element.querySelectorAll('.Post-body img')) {
        if (node.getAttribute('src') !== image.thumb) continue;
        const anchor = node.closest('a');
        if (!anchor) continue;
        if (image.deleted) {
          const placeholder = document.createElement('span');
          placeholder.className = 'CCOT-image-deleted';
          placeholder.textContent = t('lib.post_images_deleted');
          anchor.replaceWith(placeholder);
          continue;
        }
        anchor.classList.add('CCOT-post-image');
        anchor.onclick = event => {
          event.preventDefault();
          const gallery = images.filter(row => !row.deleted && row.image);
          app.modal.show(PhotoModal, {
            images: gallery,
            index: gallery.indexOf(image)
          });
        };
        node.onerror = () => {
          node.alt = t('lib.post_images_blocked');
          node.classList.add('CCOT-image-unavailable');
        };
      }
    }
    // Only an image-only native paragraph becomes a gallery. Historical inline content stays in place.
    for (const paragraph of this.element.querySelectorAll('.Post-body p')) {
      if (paragraph.querySelector('.CCOT-post-image') && Array.from(paragraph.childNodes).every(node => node.nodeType === 3 ? !node.textContent.trim() : node.nodeName === 'BR' || node.classList?.contains('CCOT-post-image'))) paragraph.classList.add('CCOT-post-gallery');
    }
  });
}
flarum.reg.add('bushcraftcn-ccot', 'forum/postImages', installPhotoComposer);